import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  UserCheck,
  UserPlus,
  AlertTriangle,
  ClipboardList,
  ChevronRight,
  CheckCircle2,
  type LucideIcon,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { DeputyTaskData } from "@/hooks/useTasksQuery";

interface DeputyTaskCardProps {
  /** Eine Zeile der Vertretungs-Union (my_deputy_tasks, Mig 387) — TES-2075/D-F3. */
  task: DeputyTaskData;
  /** Aufgelöster Anzeigename des vertretenen Prinzipals (User bei mpp/user, Gruppe bei group). */
  principalName?: string | null;
  /** Öffnet das Ursprungs-Objekt (Mangel bzw. Prüfung) in seiner Detail-Ansicht. */
  onOpen?: (entityType: string, entityId: string) => void;
  /** TES-2083 (Typ 11): übernimmt einen claimbaren ownerlosen Gruppen-Mangel des Vertretenen
   * (bestehender Wirkungsspektrum-Claim defect_self_assign → assigned_to → Deputy). Nur für
   * task.claimable-Items verdrahtet; entity_id = Mangel-id. */
  onClaim?: (entityId: string) => void;
}

// Flache Präsentations-Helfer AUSSERHALB der Komponente (Repo-Muster, vgl. resolveNotificationVisual
// TES-2028 / 84ea32d1): halten die je-Zweig-Logik aus der Render-Funktion heraus, damit deren
// Cyclomatic/CRAP nicht kippt, sobald der CI-Coverage-Step timeoutet und fallow ohne Coverage scort.
// Jeder Helfer bleibt klein + ist über DeputyTaskCard.test.tsx gedeckt.

/** Ebene mpp + Prüfung ⇒ Verifizieren-Pfad (statt reinem Objekt-Öffnen). */
function isDeputyVerification(task: DeputyTaskData): boolean {
  return task.deputy_level === "mpp" && task.entity_type === "inspection";
}

interface DeputyTypeMeta {
  typeLabel: string;
  TypeIcon: LucideIcon;
  title: string;
}

/** Typ-abhängige Anzeige: Label + Icon (Mangel/Prüfung) und Titel-Fallback auf das Typ-Label. */
function deputyTypeMeta(task: DeputyTaskData, t: TFunction): DeputyTypeMeta {
  const isDefect = task.entity_type === "defect";
  const typeLabel = isDefect ? t("tasks.deputy.typeDefect") : t("tasks.deputy.typeInspection");
  return {
    typeLabel,
    TypeIcon: isDefect ? AlertTriangle : ClipboardList,
    title: task.title || typeLabel,
  };
}

/** Aktions-Label: Verifizieren-Pfad für Ebene mpp (Prüfung), sonst Objekt (Mangel/Prüfung) öffnen. */
function deputyActionLabel(task: DeputyTaskData, isVerification: boolean, t: TFunction): string {
  if (isVerification) return t("tasks.deputy.actionVerify");
  return task.entity_type === "defect" ? t("tasks.openDefect") : t("tasks.openInspection");
}

/**
 * Lokalisiertes Status-Label des Ursprungs-Items — Mangel-Status via `defects.status.*`,
 * Prüfungs-Status via `inspections.status.*` (identisches in_progress→inProgress-Mapping wie
 * DefectStatusBadge/StatusBadge). Rohwert nur als defaultValue, falls (theoretisch) kein Key
 * existiert — alle realen Union-Status haben Keys in de/en/ja. null ⇒ keine Status-Badge.
 */
function deputyStatusLabel(task: DeputyTaskData, t: TFunction): string | null {
  if (!task.status) return null;
  const statusKey = task.status === "in_progress" ? "inProgress" : task.status;
  const base = task.entity_type === "defect" ? "defects.status" : "inspections.status";
  return t(`${base}.${statusKey}`, { defaultValue: task.status });
}

interface DeputyTaskActionProps {
  task: DeputyTaskData;
  isVerification: boolean;
  actionLabel: string;
  onOpen?: (entityType: string, entityId: string) => void;
}

/**
 * Je-Typ-Aktion (Verifizieren bzw. Objekt öffnen). Presentational — ohne `onOpen` rendert die
 * Karte keinen Button (der Parent verdrahtet die Detail-Route wie die Nachbar-Karten). Als eigene
 * Komponente ausgelagert, damit die drei Aktions-Zweige die Karten-Komplexität nicht erhöhen.
 */
function DeputyTaskAction({ task, isVerification, actionLabel, onOpen }: DeputyTaskActionProps) {
  if (!onOpen) return null;
  return (
    <Button
      variant={isVerification ? "default" : "ghost"}
      size="sm"
      className="shrink-0"
      data-testid={`deputy-task-open-${task.entity_id}`}
      onClick={() => onOpen(task.entity_type, task.entity_id)}
    >
      {isVerification && <CheckCircle2 className="mr-1 h-4 w-4" aria-hidden="true" />}
      {actionLabel}
      <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
    </Button>
  );
}

/**
 * TES-2083 (Typ 11): "Übernehmen"-Aktion für einen claimbaren ownerlosen Gruppen-Mangel des Vertretenen.
 * Löst den bestehenden Wirkungsspektrum-Claim (defect_self_assign) über den Parent aus (assigned_to →
 * Deputy). Als eigener Zweig gehalten, damit die Karten-Komplexität nicht steigt (Repo-Muster).
 */
function DeputyClaimAction({
  task,
  onClaim,
}: {
  task: DeputyTaskData;
  onClaim: (entityId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Button
      variant="default"
      size="sm"
      className="shrink-0"
      data-testid={`deputy-task-claim-${task.entity_id}`}
      onClick={() => onClaim(task.entity_id)}
    >
      <UserPlus className="mr-1 h-4 w-4" aria-hidden="true" />
      {t("tasks.deputy.actionClaim")}
    </Button>
  );
}

interface DeputyCardActionProps extends DeputyTaskActionProps {
  /** TES-2083: verdrahtet den Wirkungsspektrum-Claim für claimbare ownerlose Gruppen-Mängel. */
  onClaim?: (entityId: string) => void;
}

/**
 * TES-2083 (Typ 11): Auswahl der je-Item-Aktion — claimbare ownerlose Gruppen-Mängel des Vertretenen
 * führen den "Übernehmen"-Claim (defect_self_assign → Deputy), alle übrigen Items den bestehenden
 * Öffnen/Verifizieren-Pfad. Als flacher Helfer AUSSERHALB der Karte gehalten (Repo-Muster), damit die
 * Claim-Verzweigung die Cyclomatic/CRAP der Render-Funktion nicht anhebt (fallow ohne Coverage im CI).
 */
function DeputyCardAction({
  task,
  isVerification,
  actionLabel,
  onOpen,
  onClaim,
}: DeputyCardActionProps) {
  if (task.claimable === true && onClaim) {
    return <DeputyClaimAction task={task} onClaim={onClaim} />;
  }
  return (
    <DeputyTaskAction
      task={task}
      isVerification={isVerification}
      actionLabel={actionLabel}
      onOpen={onOpen}
    />
  );
}

/**
 * "Aufgaben als Stellvertreter"-Karte (TES-2076 · LH §L6.3): zeigt WEN der Caller vertritt und auf
 * welcher Ebene (MPP/User/Gruppe), plus das Ursprungs-Item und eine je-Typ-Aktion. Presentational —
 * Navigation/Aktion wird via onOpen delegiert (Parent verdrahtet die Detail-Route wie die Nachbar-Karten).
 *
 * Aktions-Modell (bewusst erweiterbar für die je-Typ-Issues, TES-2059 ff.): zu-verifizierende Prüfungen
 * (deputy_level='mpp', entity_type='inspection') führen über den bestehenden Verifizieren-Pfad in der
 * Prüfungs-Detailansicht; alle übrigen Items öffnen zunächst nur das Objekt (verteilen/claimen sind noch
 * nicht gebaut). Es werden KEINE neuen Backend-Aktionen erfunden.
 */
export function DeputyTaskCard({ task, principalName, onOpen, onClaim }: DeputyTaskCardProps) {
  const { t } = useTranslation();

  const isVerification = isDeputyVerification(task);
  const { typeLabel, TypeIcon, title } = deputyTypeMeta(task, t);
  const levelLabel = t(`tasks.deputy.level.${task.deputy_level}`, task.deputy_level);
  const statusLabel = deputyStatusLabel(task, t);
  const actionLabel = deputyActionLabel(task, isVerification, t);

  return (
    <Card
      data-testid={`deputy-task-card-${task.entity_id}`}
      className="transition-colors hover:bg-muted/40"
    >
      <CardContent className="flex items-center gap-3 p-4">
        <UserCheck className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="shrink-0">
              <TypeIcon className="mr-1 h-3 w-3" aria-hidden="true" />
              {typeLabel}
            </Badge>
            {/* Vertretene Ebene (MPP/User/Gruppe) */}
            <Badge variant="outline" className="shrink-0" data-testid="deputy-level-badge">
              {levelLabel}
            </Badge>
            {statusLabel && (
              <Badge variant="outline" className="shrink-0">
                {statusLabel}
              </Badge>
            )}
          </div>
          <p className="mt-1 truncate font-medium" title={title}>
            {title}
          </p>
          {/* Prinzipal: WEN vertrete ich */}
          <p className="truncate text-sm text-muted-foreground">
            {t("tasks.deputy.principalLabel")}:{" "}
            <span className="font-medium text-foreground">{principalName || "—"}</span>
          </p>
        </div>
        <DeputyCardAction
          task={task}
          isVerification={isVerification}
          actionLabel={actionLabel}
          onOpen={onOpen}
          onClaim={onClaim}
        />
      </CardContent>
    </Card>
  );
}
